/**
 * Unified commerce events — ONE function per funnel step, THREE destinations.
 *
 *   add_to_cart     → Meta AddToCart (pixel + CAPI) · Clarity add_to_cart · server `atc`
 *   begin_checkout  → Meta InitiateCheckout          · Clarity begin_checkout · server `initiate_checkout`
 *   purchase        → Meta Purchase (+Subscribe)     · Clarity purchase       · (server: Stripe webhook is truth)
 *
 * Oct 2026 (P2 brief) — Clarity reported add-to-cart at 1.15% of sessions
 * while Meta logged 454 paid-traffic ATCs in the same window, because
 * Clarity had no custom event wired. Firing all destinations from the
 * same call site guarantees the three systems count the same moments.
 *
 * Oct 2026 — Google Ads (gtag.js, AW-18371049243) added as a 4th
 * destination with the standard ecommerce event names so conversion
 * actions can be built on `purchase` / `begin_checkout` / `add_to_cart`.
 *
 * Every destination is fire-and-forget and swallows its own errors.
 */
import { isInternalSession, isPreviewHost } from "@/lib/session";

const GOOGLE_ADS_ID = "AW-18371049243";

function gtagEvent(name, params) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (isInternalSession() || isPreviewHost()) return;
  try { window.gtag("event", name, { send_to: GOOGLE_ADS_ID, currency: "AUD", ...params }); } catch { /* silent */ }
}

const gaItem = (i) => ({
  item_id: i.slug, item_name: i.name, item_category: i.kind, price: Number(i.price) || 0, quantity: Number(i.quantity) || 1,
});
import { trackAddToCart, trackInitiateCheckout, trackPurchase } from "@/lib/metaPixel";
import { trackServerATC, trackServerInitiateCheckout } from "@/lib/serverAnalytics";
import { clarityEvent } from "@/lib/clarity";

const money = (n) => (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);

/** @param {object} item { slug, kind, name, price, quantity } */
export function commerceAddToCart(item) {
  if (!item) return;
  const quantity = Number(item.quantity) || 1;
  const value = (Number(item.price) || 0) * quantity;
  try { trackAddToCart({ ...item, quantity }); } catch { /* silent */ }
  try {
    clarityEvent("add_to_cart", {
      atc_item: item.name,
      atc_kind: item.kind,
      atc_value: money(value),
    });
  } catch { /* silent */ }
  try {
    trackServerATC({ subtotal: value, itemCount: quantity, itemName: item.name, itemSlug: item.slug });
  } catch { /* silent */ }
  gtagEvent("add_to_cart", { value: Number(money(value)), items: [gaItem({ ...item, quantity })] });
}

/** @param {object} cart { items, total, subscription } */
export function commerceBeginCheckout(cart) {
  const items = cart?.items || [];
  const total = Number(cart?.total) || 0;
  try { trackInitiateCheckout({ items, total, subscription: !!cart?.subscription }); } catch { /* silent */ }
  try {
    clarityEvent("begin_checkout", {
      checkout_value: money(total),
      checkout_items: String(items.length),
      checkout_box: items.find((i) => i.kind === "box")?.name || (items.length ? "Build Your Own" : ""),
      checkout_subscription: cart?.subscription ? "yes" : "no",
    });
  } catch { /* silent */ }
  try { trackServerInitiateCheckout({ subtotal: total, itemCount: items.length }); } catch { /* silent */ }
  gtagEvent("begin_checkout", { value: Number(money(total)), items: items.map(gaItem) });
}

/** Dedicated Google Ads conversion ping — only when the owner has pasted
 *  the Purchase conversion label into Admin → Settings (served by
 *  GET /api/checkout/config as `google_ads_purchase_send_to`). */
export function commerceAdsConversion(order, sendTo) {
  if (!order || !sendTo) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (isInternalSession() || isPreviewHost()) return;
  try {
    const payload = {
      send_to: sendTo,
      value: Number(money(order.amount)),
      currency: "AUD",
      transaction_id: order.order_id || undefined,
    };
    if (typeof order.new_customer === "boolean") payload.new_customer = order.new_customer;
    window.gtag("event", "conversion", payload);
  } catch { /* silent */ }
}

/** @param {object} order { order_id, amount, items, subscription } */
export function commercePurchase(order) {
  if (!order) return null;
  let eventId = null;
  try { eventId = trackPurchase(order); } catch { /* silent */ }
  try {
    const items = order.items || [];
    clarityEvent("purchase", {
      purchase_value: money(order.amount),
      purchase_order: order.order_id || "",
      purchase_box: items.find((i) => i.kind === "box")?.name || (items.length ? "Build Your Own" : ""),
      purchase_subscription: order.subscription ? "yes" : "no",
    });
  } catch { /* silent */ }
  gtagEvent("purchase", {
    transaction_id: order.order_id || undefined,
    value: Number(money(order.amount)),
    items: (order.items || []).map(gaItem),
  });
  return eventId;
}
